import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";
import { bindTomSelectClipboardSync } from "../legacy/search";

const configCache = new Map();

function blank(value) {
  return value === null || value === undefined || String(value) === "";
}

export function normalizeFormatOptions(config, allowedFormats) {
  const allowed = new Set((allowedFormats || []).map((format) => String(format)));
  const seen = new Set();
  const options = [];

  (config.groups || []).forEach((group) => {
    (group.formats || []).forEach((format) => {
      const canonicalId = String(format.id);
      if (!allowed.has(canonicalId)) return;
      seen.add(canonicalId);

      options.push({
        occurrenceId: `${group.id}:${canonicalId}`,
        canonicalId,
        label: format.label,
        aliases: format.aliases || [],
        groupId: group.id,
        groupLabel: group.label,
        groupAliases: group.aliases || [],
        groupDescription: group.description || "",
        searchText: [
          canonicalId,
          format.label,
          ...(format.aliases || []),
          group.label,
          ...(group.aliases || []),
          group.description || "",
        ].join(" ").toLowerCase(),
      });
    });
  });

  allowed.forEach((canonicalId) => {
    if (seen.has(canonicalId)) return;
    const label = canonicalId.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
    options.push({
      occurrenceId: `ungrouped:${canonicalId}`,
      canonicalId,
      label,
      aliases: [],
      groupId: "ungrouped",
      groupLabel: "Other",
      groupAliases: [],
      groupDescription: "",
      searchText: [canonicalId, label, "Other"].join(" ").toLowerCase(),
    });
  });

  return options;
}

export default class extends Controller {
  static values = {
    url: { type: String, default: "/deck_formats.json" },
    formats: Array,
  };

  connect() {
    this.initialValues = this.selectedCanonicalValues();
    this.allowedFormats = this.formatsValue.length ? this.formatsValue : this.nativeOptionValues();

    this.loadConfig()
      .then((config) => {
        if (!this.element.isConnected) return;
        this.initTomSelect(normalizeFormatOptions(config, this.allowedFormats));
      })
      .catch(() => {
        this.destroyTomSelect();
      });
  }

  disconnect() {
    this.destroyTomSelect();
  }

  loadConfig() {
    const url = this.urlValue;
    if (!configCache.has(url)) {
      configCache.set(
        url,
        fetch(url, { headers: { Accept: "application/json" } }).then((response) => {
          if (!response.ok) throw new Error(`Format config failed: ${response.status}`);
          return response.json();
        }).catch((error) => {
          configCache.delete(url);
          throw error;
        })
      );
    }
    return configCache.get(url);
  }

  selectedCanonicalValues() {
    return Array.from(this.element.selectedOptions)
      .map((option) => option.value)
      .filter((value) => !blank(value));
  }

  nativeOptionValues() {
    return Array.from(this.element.options)
      .map((option) => option.value)
      .filter((value) => !blank(value));
  }

  initTomSelect(options) {
    const selectedOccurrences = this.initialValues
      .map((canonicalId) => options.find((option) => option.canonicalId === canonicalId)?.occurrenceId)
      .filter(Boolean);
    const optgroups = [];
    const seenOptgroups = new Set();
    options.forEach((option) => {
      if (seenOptgroups.has(option.groupId)) return;
      seenOptgroups.add(option.groupId);
      optgroups.push({ id: option.groupId, label: option.groupLabel });
    });

    this.tomSelect = new TomSelect(this.element, {
      options,
      optgroups,
      valueField: "occurrenceId",
      labelField: "label",
      optgroupField: "groupId",
      optgroupValueField: "id",
      optgroupLabelField: "label",
      searchField: ["searchText"],
      maxItems: this.element.multiple ? null : 1,
      maxOptions: 500,
      items: selectedOccurrences,
      plugins: this.element.multiple ? ["remove_button", "clear_button"] : ["clear_button"],
      create: false,
      hideSelected: false,
      onChange: () => this.handleChange(),
      score: (search) => {
        const query = String(search || "").trim().toLowerCase();
        return (item) => {
          if (!query) return 1;
          return item.searchText.includes(query) ? 1 : 0;
        };
      },
      render: {
        option: (item, escape) => {
          return `<div>${escape(item.label)}</div>`;
        },
        optgroup_header: (group, escape) => {
          return `<div class="format-tree-select-group-header">${escape(group.label)}</div>`;
        },
        item: (item, escape) => {
          return `<div>${escape(item.label)}</div>`;
        },
      },
    });

    this.tomSelect.wrapper.classList.remove("form-select");
    this.installCanonicalSubmission();
    this.syncCanonicalValues();
    bindTomSelectClipboardSync(this.element);
  }

  handleChange() {
    this.syncCanonicalValues();

    if (this.tomSelect && !this.element.multiple) {
      this.tomSelect.close();
      this.tomSelect.blur();
    }
  }

  installCanonicalSubmission() {
    this.originalName = this.element.getAttribute("name");
    if (!this.originalName) return;

    this.element.removeAttribute("name");
    this.hiddenContainer = document.createElement("span");
    this.hiddenContainer.hidden = true;
    this.element.insertAdjacentElement("afterend", this.hiddenContainer);
  }

  syncCanonicalValues() {
    if (!this.tomSelect) return;

    const selected = Array.isArray(this.tomSelect.items) ? this.tomSelect.items : [this.tomSelect.items];
    const canonicalIds = [];
    selected.forEach((occurrenceId) => {
      const option = this.tomSelect.options[occurrenceId];
      if (option && !canonicalIds.includes(option.canonicalId)) {
        canonicalIds.push(option.canonicalId);
      }
    });

    Array.from(this.element.options).forEach((option) => {
      option.selected = canonicalIds.includes(option.value);
    });

    if (!this.element.multiple && canonicalIds.length === 0) {
      this.element.value = "";
    }

    this.syncHiddenInputs(canonicalIds);
  }

  syncHiddenInputs(canonicalIds) {
    if (!this.hiddenContainer || !this.originalName) return;

    this.hiddenContainer.innerHTML = "";
    const values = this.element.multiple ? canonicalIds : [canonicalIds[0] || ""];
    values.forEach((value) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = this.originalName;
      input.value = value;
      this.hiddenContainer.appendChild(input);
    });
  }

  destroyTomSelect() {
    if (this.originalName && !this.element.getAttribute("name")) {
      this.element.setAttribute("name", this.originalName);
    }
    if (this.hiddenContainer) {
      this.hiddenContainer.remove();
      this.hiddenContainer = null;
    }
    if (this.tomSelect) {
      this.tomSelect.destroy();
      this.tomSelect = null;
    }
  }
}
